import { z } from 'zod';
import { ActionIntent } from './actions.ts';
import { Id, JsonObject, Sha256Hex } from './common.ts';
import { ErrorCode } from './errors.ts';
import { Evidence } from './evidence.ts';
import { hashCanonical } from './hash.ts';

export const ValidationFinding = z
  .object({
    level: z.enum(['info', 'warning', 'error']),
    code: z.string().min(1),
    message: z.string(),
    /** JSON path or action id the finding refers to. */
    path: z.string().optional(),
  })
  .meta({ id: 'ValidationFinding' });
export type ValidationFinding = z.infer<typeof ValidationFinding>;

/** Output of a reasoning episode: what the agent proposes, with evidence and actions. */
export const Proposal = z
  .object({
    proposalId: Id,
    taskId: Id,
    summary: z.string(),
    structuredOutput: JsonObject,
    evidence: z.array(Evidence).default([]),
    unresolvedQuestions: z.array(z.string()).default([]),
    actions: z.array(ActionIntent).default([]),
    validationFindings: z.array(ValidationFinding).default([]),
    versions: z.object({
      model: z.string(),
      prompt: z.string(),
      tools: z.record(z.string(), z.string()).default({}),
    }),
    /** `computeProposalHash(proposal)`, filled by the reasoning runtime; approvals bind to it. */
    proposalHash: Sha256Hex.optional(),
  })
  .meta({ id: 'Proposal' });
export type Proposal = z.infer<typeof Proposal>;

/**
 * The hash an approval binds to:
 * `hashCanonical({ taskId, summary, structuredOutput, actions: [{ actionId, tool, toolVersion, target, argsHash }] })`.
 * Evidence, findings and versions are excluded so re-validation does not invalidate an approval.
 */
export function computeProposalHash(
  proposal: Pick<Proposal, 'taskId' | 'summary' | 'structuredOutput' | 'actions'>,
): string {
  return hashCanonical({
    taskId: proposal.taskId,
    summary: proposal.summary,
    structuredOutput: proposal.structuredOutput,
    actions: proposal.actions.map((a) => ({
      actionId: a.actionId,
      tool: a.tool,
      toolVersion: a.toolVersion,
      target: a.target,
      argsHash: a.argsHash,
    })),
  });
}

export const ClarificationQuestion = z
  .object({
    id: z.string().min(1),
    text: z.string().min(1),
    /** Input field the answer fills, when applicable. */
    field: z.string().optional(),
    options: z.array(z.string()).optional(),
  })
  .meta({ id: 'ClarificationQuestion' });
export type ClarificationQuestion = z.infer<typeof ClarificationQuestion>;

export const EpisodeUsage = z
  .object({
    modelCalls: z.number().int().nonnegative(),
    toolCalls: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    durationMs: z.number().nonnegative(),
  })
  .meta({ id: 'EpisodeUsage' });
export type EpisodeUsage = z.infer<typeof EpisodeUsage>;

/** Result of `ReasoningRuntime.runEpisode`. */
export const AgentResult = z
  .object({
    status: z.enum(['proposed', 'needs_clarification', 'abstained', 'failed']),
    proposal: Proposal.optional(),
    questions: z.array(ClarificationQuestion).optional(),
    error: z.object({ code: ErrorCode, message: z.string() }).optional(),
    /** Why the episode ended without a usable proposal, in the agent's words; set for `abstained`. */
    reason: z.string().optional(),
    usage: EpisodeUsage,
  })
  .meta({ id: 'AgentResult' });
export type AgentResult = z.infer<typeof AgentResult>;
